import { Button } from '@up2stay/up2promo-ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { matchesConfirmName } from '@/core/components/settings/confirm-name-match';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface DangerZoneProps {
  sectionId?: string;
  title: string;
  /** Short irreversible warning — shown as SettingsGroupFooter. */
  description: string;
  deleteTitle: string;
  /**
   * Longer consequences — shown via SettingsInfoHint when distinct from
   * `description`. Dialog copy stays the primary confirm safety text.
   */
  deleteDescription: string;
  deleteButtonLabel: string;
  onDelete: () => Promise<void>;
  /** Specific message for a known failure; falls back to `dict.error`. */
  resolveErrorMessage?: (error: unknown) => string | null;
  confirmName: string;
  expandLabel?: string;
  collapseLabel?: string;
  dict: {
    dialogTitle: string;
    dialogDescription: string;
    confirmLabel: string;
    confirmPlaceholder: string;
    cancel: string;
    confirm: string;
    nameMismatch: string;
    success: string;
    error: string;
  };
}

export function DangerZone({
  sectionId = 'danger-zone',
  title,
  description,
  deleteTitle,
  deleteDescription,
  deleteButtonLabel,
  onDelete,
  resolveErrorMessage,
  confirmName,
  expandLabel = 'Show destructive actions',
  collapseLabel = 'Hide destructive actions',
  dict,
}: DangerZoneProps) {
  const [open, setOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const uiDict = useDictionary().ui;
  const nameMatches = matchesConfirmName(deleteConfirmName, confirmName);

  const detailInfo =
    deleteDescription.trim() !== description.trim()
      ? deleteDescription
      : undefined;

  const handleDelete = async () => {
    if (!nameMatches) {
      toast.error(dict.nameMismatch);
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete();
      toast.success(dict.success);
      setIsDeleteDialogOpen(false);
      setDeleteConfirmName('');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(resolveErrorMessage?.(error) ?? dict.error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SettingsSection
        id={sectionId}
        title={title}
        footer={description}
        info={detailInfo}
        tone="danger"
        grouped
      >
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-between rounded-none border-0 px-4 py-3.5 text-left font-medium text-destructive shadow-none hover:bg-muted/40 data-[state=open]:bg-transparent sm:px-5"
            >
              <span>{open ? collapseLabel : expandLabel}</span>
              <ChevronDown
                className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t border-border">
            <div className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
              <span className="min-w-0 text-[13.5px] font-medium leading-snug text-destructive">
                {deleteTitle}
              </span>
              <Button
                variant="destructive"
                size="sm"
                className="shrink-0"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="size-4" />
                {deleteButtonLabel}
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </SettingsSection>

      <ResponsiveDialog
        open={isDeleteDialogOpen}
        onOpenChange={(next) => {
          void triggerTapHaptic();
          setIsDeleteDialogOpen(next);
        }}
      >
        <ResponsiveDialogContent closeLabel={uiDict.close}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="text-destructive">
              {dict.dialogTitle}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {dict.dialogDescription}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <div className="space-y-4 py-4">
            {confirmName ? (
              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">
                  {uiDict.typeToConfirm}
                </p>
                <p className="select-all rounded-md bg-muted px-3 py-2 text-sm font-semibold break-words text-foreground">
                  {confirmName}
                </p>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="deleteConfirmName">{dict.confirmLabel}</Label>
              <Input
                id="deleteConfirmName"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={dict.confirmPlaceholder}
                className={!nameMatches ? 'border-destructive' : ''}
              />
              {deleteConfirmName && !nameMatches ? (
                <p className="text-sm text-destructive">{dict.nameMismatch}</p>
              ) : null}
            </div>
          </div>
          <ResponsiveDialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {dict.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              isLoading={isDeleting}
              disabled={!nameMatches}
            >
              <Trash2 className="size-4" />
              {dict.confirm}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
